import { faArrowRight, faPlusCircle, faTrash, faWrench } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { canPerformAction } from 'actions/userActions';
import { useAppDispatch } from 'hooks';
import React, { useEffect, useMemo, useState } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { availableOperations } from 'reducers/userReducer';
import * as actions from '../../../actions/nodesActions';
import useToaster from '../../../actions/useToaster';
import { ForwardRule, Node } from '../../../reducers/nodesReducer';
import { getForwardRules, getNodes, getUser } from '../../../reducers/selectors';
import ConditionsTable from '../conditions/ConditionsTable';
import EditForwardRule from './EditForwardRule';
import NewForwardRule from './NewForwardRule';
import { Link } from 'react-router-dom';

type TForwardRulesTable = {
  selectedNode: Node | null;
};

function ForwardRulesTable({ selectedNode }: TForwardRulesTable): React.ReactElement {
  const toaster = useToaster();
  const dispatch = useAppDispatch();
  const forwardRules: ForwardRule[] = useSelector(getForwardRules);
  const user = useSelector(getUser);
  const nodes = useSelector(getNodes);

  const [shownRules, setShownRules] = useState<ForwardRule[]>(forwardRules);
  const [showAllRules, setShowAllRules] = useState<boolean>(false);
  const [selectedRule, setSelectedRule] = useState<ForwardRule | null>(null);
  const [editingRule, setEditingRule] = useState<ForwardRule | null>(null);
  const [newRulePopup, showNewRulePopup] = useState<boolean>(false);
  const [deletingRule, setDeletingRule] = useState<ForwardRule | null>(null);
  const [selectedNodeRules, setSelectedNodeRules] = useState<ForwardRule[]>([]);

  const authorized = useMemo(() => canPerformAction(user, availableOperations.CAN_MANAGE_NODES), [user]);
  const scriptsAuthorized = useMemo(() => canPerformAction(user, availableOperations.SCRIPT_CATALOG_VISIBLE), [user]);

  useEffect(() => {
    setShownRules(
      [...forwardRules].sort((a, b) => {
        if (a.from > b.from) return 1;

        if (a.from < b.from) return -1;

        if (a.from === b.from) {
          if (a.to > b.to) return 1;

          if (a.to < b.to) return -1;

          return 0;
        }

        return 0;
      })
    );
  }, [forwardRules]);

  useEffect(() => {
    if (selectedNode) {
      setSelectedNodeRules(shownRules.filter(rule => rule.from === selectedNode.aeTitle));
    }
  }, [selectedNode, shownRules]);

  return (
    <div>
      <div>
        <strong>Forwarding rules</strong> <small>(studies received from node X will be forwarded to node Y)</small>
        <Form.Check
          custom
          id="showRulesForwardRulesTable"
          type="checkbox"
          style={{ float: 'right', margin: '0' }}
          checked={showAllRules}
          onChange={() => setShowAllRules(!showAllRules)}
          label="Show All"
        />
        <table className="queue-table">
          <thead>
            <tr>
              <th style={{ width: '20%' }} />
              <th style={{ width: '6%' }} />
              <th style={{ width: '20%' }} />
              <th />
              <th style={{ width: '6%' }} />
              <th />
              <th style={{ width: '16%' }} />
            </tr>
          </thead>
          <tbody>
            {!showAllRules && selectedNode == null && (
              <tr>
                <td style={{ textAlign: 'center' }} colSpan={7}>
                  Select a node
                </td>
              </tr>
            )}

            {!showAllRules && selectedNode && selectedNodeRules.length === 0 && (
              <tr>
                <td style={{ textAlign: 'center' }} colSpan={7}>
                  {selectedNode.aeTitle} has no rule
                </td>
              </tr>
            )}

            {!showAllRules &&
              selectedNode &&
              selectedNodeRules.length > 0 &&
              selectedNodeRules.map((rule, index) => (
                <ForwardRuleRow
                  key={index}
                  rule={rule}
                  onEdit={() => setEditingRule(rule)}
                  onDelete={() => setDeletingRule(rule)}
                  authorized={authorized}
                  scriptsAuthorized={scriptsAuthorized}
                  onClick={() => {
                    setSelectedRule(rule);
                    dispatch(actions.isTouchedRule());
                  }}
                />
              ))}

            {showAllRules && shownRules.length === 0 && (
              <tr>
                <td style={{ textAlign: 'center' }} colSpan={7}>
                  No rule configured
                </td>
              </tr>
            )}
            {showAllRules &&
              shownRules.length > 0 &&
              shownRules.map((rule, index) => (
                <ForwardRuleRow
                  key={index}
                  rule={rule}
                  onEdit={() => setEditingRule(rule)}
                  onDelete={() => setDeletingRule(rule)}
                  authorized={authorized}
                  scriptsAuthorized={scriptsAuthorized}
                  onClick={() => {
                    setSelectedRule(rule);
                    dispatch(actions.isTouchedRule());
                  }}
                />
              ))}
          </tbody>
        </table>
        <Button
          size="sm"
          variant="primary"
          style={{
            float: 'right',
            marginLeft: '2em',
          }}
          disabled={!authorized}
          onClick={() => showNewRulePopup(true)}
        >
          Add forwarding rule
          <FontAwesomeIcon icon={faPlusCircle} style={{ marginLeft: '5px' }} />
        </Button>
      </div>

      <ConditionsTable rules={shownRules} selectedNode={selectedNode} selectedRule={selectedRule} />

      {newRulePopup && (
        <NewRulePopup
          onClose={() => showNewRulePopup(false)}
          onSave={rule => {
            actions
              .insertForwardRule(rule)
              .then(() => {
                toaster.success('Forward rule created');
                showNewRulePopup(false);
                dispatch(actions.forceGetForwardRules());
              })
              .catch(error => {
                console.error(error);
                toaster.error('Error creating rule');
              });
          }}
        />
      )}
      {editingRule && (
        <EditRulePopup
          rule={editingRule}
          onClose={() => setEditingRule(null)}
          onSave={rule => {
            actions
              .updateForwardRule(rule)
              .then(() => {
                toaster.success('Forward rule updated');
                setEditingRule(null);
                dispatch(actions.forceGetForwardRules());
              })
              .catch(error => {
                console.error(error);
                toaster.error('Error updating rule');
              });
          }}
        />
      )}
      {deletingRule && (
        <DeleteRulePopup
          rule={deletingRule}
          onClose={() => setDeletingRule(null)}
          onConfirmDelete={() => {
            const sourceNode = nodes.find(node => node.aeTitle === deletingRule.from);
            const destinationNode = nodes.find(node => node.aeTitle === deletingRule.to);
            const rule = {
              source: sourceNode!.pk,
              destination: destinationNode!.pk,
            };

            actions
              .deleteForwardRule(rule)
              .then(() => {
                toaster.success('Forward rule deleted');
                setDeletingRule(null);
                dispatch(actions.forceGetForwardRules());
              })
              .catch(error => {
                console.error(error);
                toaster.error('Error deleting rule, maybe there are some conditions attached');
              });
          }}
        />
      )}
    </div>
  );
}

interface IForwardRule {
  rule: ForwardRule;
  onEdit: () => void;
  onDelete: () => void;
  authorized: boolean;
  scriptsAuthorized: boolean;
  onClick: () => void;
}

const ForwardRuleRow = ({ rule, onEdit, onDelete, authorized, scriptsAuthorized, onClick }: IForwardRule) => (
  <tr className="node-row" onClick={onClick}>
    <td title={rule.from}>{rule.from}</td>
    <td>
      <FontAwesomeIcon icon={faArrowRight} />
    </td>
    <td title={rule.to}>{rule.to}</td>
    <td title={rule.temporalWindow}>{rule.temporalWindow}</td>
    <td>{rule.queue}</td>
    <td title={(rule.morphingScript || 'no morphing') + ', ' + (rule.filterScript || 'no filter')}>
      {scriptsAuthorized ? (
        <React.Fragment>
          {[rule.morphingScript, rule.filterScript].filter(Boolean).map(script => (
            <Button key={script} variant="link" size="sm" as={Link} to={`/scripts?name=${script}`}>
              {script}
            </Button>
          ))}
        </React.Fragment>
      ) : (
        <React.Fragment>{[rule.morphingScript, rule.filterScript].filter(Boolean).join(', ')}</React.Fragment>
      )}
    </td>
    <td>
      <div style={{ width: '100%', textAlign: 'right', overflow: 'visible' }}>
        {authorized && <FontAwesomeIcon icon={faWrench} className="clickable-icon" onClick={onEdit} />}
        {authorized && <FontAwesomeIcon icon={faTrash} className="clickable-icon" onClick={onDelete} />}
      </div>
    </td>
  </tr>
);

const NewRulePopup = ({ onClose, onSave }) => (
  <Modal show size="lg">
    <Modal.Header>
      <Modal.Title>New forward rule</Modal.Title>
    </Modal.Header>
    <Modal.Body>
      <NewForwardRule onClose={onClose} onSave={onSave} />
    </Modal.Body>
  </Modal>
);

const EditRulePopup = ({ rule, onClose, onSave }) => (
  <Modal show size="lg">
    <Modal.Header>
      <Modal.Title>Editing forward rule</Modal.Title>
    </Modal.Header>
    <Modal.Body>
      <EditForwardRule rule={rule} onClose={onClose} onSave={onSave} />
    </Modal.Body>
  </Modal>
);

const DeleteRulePopup = ({ rule, onClose, onConfirmDelete }) => (
  <Modal show>
    <Modal.Header>
      <Modal.Title>Deleting forward rule</Modal.Title>
    </Modal.Header>
    <Modal.Body>
      <p>
        Deleting forward rule from {rule.from} to {rule.to}
      </p>
      <p>Are you sure?</p>
      <div style={{ textAlign: 'right' }}>
        <Button onClick={onClose} variant="outline-secondary" style={{ marginRight: '5px' }}>
          Cancel
        </Button>
        <Button variant="danger" onClick={onConfirmDelete}>
          Confirm delete
        </Button>
      </div>
    </Modal.Body>
  </Modal>
);

export default ForwardRulesTable;
